(() => {
  document.documentElement.classList.remove('no-js');

  class ImageComparison extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('[data-comparison-range]');
      if (!this.range) return;
      this.update = this.update.bind(this);
      this.range.addEventListener('input', this.update);
      this.update();
    }

    update() {
      const position = this.range.value;
      this.style.setProperty('--comparison-position', `${position}%`);
      this.range.setAttribute('aria-valuetext', `${position}% revealed`);
    }
  }

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.root = this.closest('[data-main-product]');
      this.form = this.querySelector('form[action*="/cart/add"]');
      this.variantInput = this.querySelector('[data-variant-id]');
      this.optionFields = Array.from(this.querySelectorAll('[data-product-option]'));
      this.optionInputs = this.querySelectorAll('[data-option-input]');
      this.checkoutButton = this.querySelector('[data-product-checkout]');
      this.variants = this.readVariants();
      const signal = this.controller.signal;
      this.optionInputs.forEach((input) => input.addEventListener('change', (event) => this.selectOption(event), { signal }));
      this.checkoutButton?.addEventListener('click', () => this.goToCheckout(), { signal });
      window.addEventListener('popstate', () => this.restoreFromUrl(), { signal });
      window.addEventListener('pageshow', () => this.syncVariantInput(), { signal });
      this.form?.addEventListener('submit', () => this.syncVariantInput(), { capture: true, signal });
      this.addEventListener('click', () => this.syncVariantInput(), { capture: true, signal });
      const linkedId = new URL(window.location.href).searchParams.get('variant');
      const selectedOptions = this.optionFields.map((fieldset) => fieldset.querySelector('[data-option-input]:checked')?.value);
      const initialVariant = this.variants.find((variant) => String(variant.id) === String(linkedId || this.variantInput?.value))
        || this.variants.find((variant) => variant.options.every((option, index) => option === selectedOptions[index]));
      if (initialVariant) this.applyVariant(initialVariant, { historyMode: null, changedOptionName: null });
      if (this.variantInput) {
        this.variantObserver = new MutationObserver(() => this.syncVariantInput());
        this.variantObserver.observe(this.variantInput, { attributes: true, attributeFilter: ['value'] });
      }
      requestAnimationFrame(() => this.syncVariantInput());
      window.setTimeout(() => this.syncVariantInput(), 250);
      window.setTimeout(() => this.syncVariantInput(), 1000);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.variantObserver?.disconnect();
    }

    readVariants() {
      const source = this.root?.querySelector('[data-product-variants]');
      if (!source) return [];
      try {
        return JSON.parse(source.textContent);
      } catch (error) {
        return [];
      }
    }

    selectOption(event) {
      const selectedInput = event.currentTarget;
      const selectedOptions = this.optionFields.map((fieldset) => fieldset.querySelector('[data-option-input]:checked')?.value);
      let variant = this.variants.find((item) => item.options.every((option, index) => option === selectedOptions[index]));
      if (!variant) {
        const changedPosition = Number(selectedInput.closest('[data-product-option]')?.dataset.optionPosition || 0) - 1;
        const editionPosition = Number(this.root?.dataset.editionPosition || 0) - 1;
        const preferredEdition = selectedOptions[editionPosition];
        variant = this.variants.find((item) => item.options[editionPosition] === preferredEdition && item.available)
          || this.variants.find((item) => item.options[changedPosition] === selectedInput.value && item.available)
          || this.variants.find((item) => item.options[editionPosition] === preferredEdition)
          || this.variants[0];
      }
      if (!variant) return;
      const changedOptionName = selectedInput.closest('[data-product-option]')?.dataset.optionName || null;
      this.applyVariant(variant, { historyMode: 'push', changedOptionName });
    }

    applyVariant(variant, { historyMode, changedOptionName }) {
      this.currentVariant = variant;
      this.optionFields.forEach((fieldset, index) => {
        const value = variant.options[index];
        const input = Array.from(fieldset.querySelectorAll('[data-option-input]')).find((candidate) => candidate.value === value);
        if (input) input.checked = true;
        const selectedLabel = fieldset.querySelector('legend span');
        if (selectedLabel) selectedLabel.textContent = value || '';
      });
      this.syncVariantInput();
      const purchaseButtons = Array.from(this.querySelectorAll('[data-purchase-button]'));
      purchaseButtons.forEach((button) => {
        button.disabled = !variant.available;
        const label = button.querySelector('[data-purchase-label]');
        if (label) label.textContent = variant.available ? button.dataset.availableLabel : button.dataset.soldOutLabel;
      });
      const status = this.querySelector('.product-form__availability');
      if (status && this.checkoutButton) status.textContent = variant.available ? this.checkoutButton.dataset.availableStatus : this.checkoutButton.dataset.unavailableStatus;
      const price = this.root?.querySelector('[data-product-price]');
      if (price && Number.isFinite(Number(variant.price))) price.textContent = this.formatMoney(Number(variant.price));
      const afterpayPlacement = this.root?.querySelector('[data-afterpay-product-placement]');
      if (afterpayPlacement && Number.isFinite(Number(variant.price))) {
        afterpayPlacement.setAttribute('data-item-skus', variant.sku || String(variant.id));
        afterpayPlacement.setAttribute('data-amount', (Number(variant.price) / 100).toFixed(2));
      }
      if (historyMode) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        const nextUrl = `${url.pathname}${url.search}${url.hash}`;
        if (historyMode === 'push' && String(new URL(window.location.href).searchParams.get('variant')) !== String(variant.id)) {
          window.history.pushState({ variantId: variant.id }, '', nextUrl);
        } else if (historyMode === 'replace') {
          window.history.replaceState({ variantId: variant.id }, '', nextUrl);
        }
      }
      const editionPosition = Number(this.root?.dataset.editionPosition || 0) - 1;
      const sizePosition = Number(this.root?.dataset.sizePosition || 0) - 1;
      const detail = {
        variant,
        edition: editionPosition >= 0 ? variant.options[editionPosition] : null,
        size: sizePosition >= 0 ? variant.options[sizePosition] : null,
        changedOptionName
      };
      this.root?.dispatchEvent(new CustomEvent('product:variant-change', { bubbles: true, detail }));
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail }));
    }

    syncVariantInput() {
      const variantId = this.currentVariant?.id;
      if (!this.variantInput || !variantId) return;
      const value = String(variantId);
      if (this.variantInput.value !== value) this.variantInput.value = value;
      if (this.variantInput.getAttribute('value') !== value) this.variantInput.setAttribute('value', value);
    }

    async goToCheckout() {
      if (!this.form || !this.checkoutButton || this.checkoutButton.disabled) return;
      this.syncVariantInput();
      const errorMessage = this.querySelector('[data-product-form-error]');
      if (errorMessage) {
        errorMessage.hidden = true;
        errorMessage.textContent = '';
      }
      const purchaseButtons = Array.from(this.querySelectorAll('[data-purchase-button]'));
      purchaseButtons.forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      });
      try {
        const formData = new FormData(this.form);
        if (this.currentVariant?.id) formData.set('id', String(this.currentVariant.id));
        if (!formData.has('quantity')) formData.set('quantity', '1');
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'This selection could not be added.');
        window.location.assign(`${root}checkout`);
      } catch (error) {
        purchaseButtons.forEach((button) => {
          button.disabled = !this.currentVariant?.available;
          button.removeAttribute('aria-busy');
        });
        if (errorMessage) {
          errorMessage.textContent = error.message;
          errorMessage.hidden = false;
        }
      }
    }

    restoreFromUrl() {
      const linkedId = new URL(window.location.href).searchParams.get('variant');
      let variant = this.variants.find((item) => String(item.id) === String(linkedId));
      if (!variant) {
        const editionPosition = Number(this.root?.dataset.editionPosition || 0) - 1;
        const sizePosition = Number(this.root?.dataset.sizePosition || 0) - 1;
        const defaultEdition = this.root?.dataset.defaultEdition;
        const currentSize = sizePosition >= 0 ? this.currentVariant?.options[sizePosition] : null;
        variant = this.variants.find((item) => item.options[editionPosition] === defaultEdition && item.options[sizePosition] === currentSize && item.available)
          || this.variants.find((item) => item.options[editionPosition] === defaultEdition && item.available)
          || this.variants.find((item) => item.options[editionPosition] === defaultEdition)
          || this.variants[0];
      }
      if (variant) this.applyVariant(variant, { historyMode: null, changedOptionName: null });
    }

    formatMoney(cents) {
      const currency = this.root?.dataset.currency || 'AUD';
      const locale = this.root?.dataset.locale || 'en-AU';
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
      } catch (error) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }
  }

  customElements.define('image-comparison', ImageComparison);
  customElements.define('product-form', ProductForm);
})();
